import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  startWith,
} from 'rxjs';
import { isPresent } from 'src/app/core/utils/isPresent';
import { ValidationErrorsService } from 'src/app/shared/services/validation-errors.service';

@UntilDestroy()
@Component({
  selector: 'app-single-select-autocomplete',
  templateUrl: './single-select-autocomplete.component.html',
  styleUrl: './single-select-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleSelectAutocompleteComponent
  implements OnInit, ControlValueAccessor, AfterViewInit, OnChanges
{
  @Input() label: string | undefined;
  @Input() autocompleteOptions: any[] = [];
  @Input() optionIcon: string = '';
  @Input() optionIconColor: string = '';
  @Input() optionLabel: string = 'name';
  @Input() optionValue: string = 'id';
  @Input() placeholderKey: string = 'placeholders.select';
  @Input() refreshCollectionByTranslationEvent: boolean = false;
  @ViewChild('optionInput')
  optionInput!: ElementRef<HTMLInputElement>;

  @Output() onSelect: EventEmitter<string> = new EventEmitter<string>();
  selectedOptionValue: any;
  filteredOptions$: Observable<any[]> | undefined;
  focusOut$: Observable<any> | undefined;
  private viewInitialized = false;

  get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }
  get isRequired(): boolean {
    const validator = this.control.validator ? this.control.validator({} as AbstractControl) : null;
    return validator && validator['required'];
  }

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private validationErrorsService: ValidationErrorsService,
    private cdr: ChangeDetectorRef,
    private translocoService: TranslocoService,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.selectedOptionValue = this.control.value;
    this.resetFilteredOptions();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.initSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.refreshCollectionByTranslationEvent || !this.viewInitialized) {
      return;
    }

    if (changes['autocompleteOptions'] || changes['optionLabel']) {
      this.refreshCollectionAndSelectedValue();
    }
  }

  initSubscriptions() {
    this.processValueChanges();
    this.processFocusOut();
    this.processTranslationChanges();
  }

  processTranslationChanges(): void {
    this.translocoService.langChanges$
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe(() => {
        if (!this.refreshCollectionByTranslationEvent) {
          return;
        }

        this.refreshCollectionAndSelectedValue();
      });
  }

  private refreshCollectionAndSelectedValue(): void {
    this.resetFilteredOptions();
    this.syncDisplayedSelectedValue();
    this.cdr.markForCheck();
  }

  private syncDisplayedSelectedValue(): void {
    if (!this.optionInput) {
      return;
    }

    this.optionInput.nativeElement.value = this.displayFn(this.control?.value);
  }

  resetFilteredOptions(): void {
    this.filteredOptions$ = this.control.valueChanges.pipe(
      startWith<string>(''),
      map((filter) => this.filter(filter)),
    );
  }

  processValueChanges(): void {
    this.control?.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged(), filter(isPresent))
      .subscribe((value) => {
        if (
          this.autocompleteOptions.findIndex(
            (item) => item[this.optionLabel] === value || item[this.optionValue] === value,
          ) === -1 &&
          this.selectedOptionValue !== null &&
          this.selectedOptionValue !== undefined
        ) {
          this.filteredOptions$ = this.control.valueChanges.pipe(
            startWith<string>(''),
            map((filter) => this.filter(filter)),
          );
          this.selectedOptionValue = undefined;
          this.onSelect.emit(undefined);
          this.control.patchValue(undefined);
          this.cdr.detectChanges();
        }
      });
  }

  processFocusOut(): void {
    this.focusOut$ = fromEvent(this.optionInput.nativeElement, 'blur');

    this.focusOut$.pipe(untilDestroyed(this), debounceTime(100)).subscribe((event: any) => {
      setTimeout(() => {
        if (
          this.autocompleteOptions.findIndex((item) => {
            return item[this.optionLabel] === this.optionInput.nativeElement.value;
          }) === -1
        ) {
          this.control.patchValue(undefined);
        } else {
          this.resetFilteredOptions();
        }
      }, 100);
    });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedOptionValue = event.option.value;
    this.control.patchValue(event.option.value);
    this.onSelect.emit(this.selectedOptionValue);
    this.cdr.detectChanges();
  }

  private filter(filter: any): any[] {
    const filterValue = filter;

    if (typeof filter === 'number') {
      filter = filter + '';
    }

    const sortedOptions = [...this.autocompleteOptions].sort((a, b) =>
      a[this.optionLabel].localeCompare(b[this.optionLabel]),
    );

    if (this.autocompleteOptions.some((option) => option[this.optionValue] === filterValue)) {
      return sortedOptions;
    }

    if (filter) {
      return this.autocompleteOptions
        .filter((option) => {
          const filterOk =
            option[this.optionLabel].toLowerCase().indexOf(filter.toLowerCase()) >= 0;
          return filterOk;
        })
        .sort((a, b) => a[this.optionLabel].localeCompare(b[this.optionLabel]));
    } else {
      return sortedOptions;
    }
  }

  displayFn(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return (
      this.autocompleteOptions.find((item) => {
        return item[this.optionValue] === value;
      })?.[this.optionLabel] || ''
    );
  }

  public getErrorMessage(): string {
    if (this.control?.invalid && this.control?.errors) {
      return this.validationErrorsService.getControlErrorMessage(this.control.errors);
    }
    return '';
  }

  public writeValue(value: any): void {}

  onChange: any = (value: string) => {};
  onTouched: any = () => {};

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
